import type { ReactElement } from 'react'
import type { ContractUIComponentData } from 'tripPlanner/api/types'
import CommunicationsPreferencesConfirmationPrompt from './CommunicationsPreferencesConfirmationPrompt'
import StagedActionConfirmationPrompt from './StagedActionConfirmationPrompt'
import type { UI_COMPONENT_TYPES } from './constants'

type ConfirmationPromptUIComponentData = Extract<
  ContractUIComponentData,
  { type: typeof UI_COMPONENT_TYPES.CONFIRMATION_PROMPT }
>

type ContractConfirmationPromptPresentation =
  ConfirmationPromptUIComponentData['presentation']

export interface ConfirmationPreviewRow {
  label: string
  currentValue: string
  nextValue: string
}

type GenericConfirmationFallback =
  ContractConfirmationPromptPresentation['fallback']

export type ConfirmationPromptPresentation =
  | {
    component: 'communications_preferences'
    props: {
      previewRows: Array<ConfirmationPreviewRow>
    }
    fallback: GenericConfirmationFallback
  }
  | ContractConfirmationPromptPresentation

export interface ConfirmationPromptRendererProps {
  pendingActionId: string
  selectedAction?: 'confirm' | 'cancel'
  isHistorical?: boolean
  presentation: ConfirmationPromptPresentation
  onSendMessage: (message: string) => void
}

type PromptRenderer = (
  props: Omit<ConfirmationPromptRendererProps, 'presentation'> & {
    presentation: ConfirmationPromptPresentation
    fallback: GenericConfirmationFallback
  },
) => ReactElement

type KnownConfirmationPromptRenderer = 'communications_preferences' | 'generic'

function isCommunicationsPreferencesPresentation(
  presentation: ConfirmationPromptPresentation,
): presentation is Extract<
  ConfirmationPromptPresentation,
  { component: 'communications_preferences' }
> {
  return (
    presentation.component === 'communications_preferences' &&
    typeof presentation.props === 'object' &&
    presentation.props !== null &&
    'previewRows' in presentation.props &&
    Array.isArray(presentation.props.previewRows)
  )
}

const knownConfirmationPromptRenderers = {
  communications_preferences: ({
    pendingActionId,
    selectedAction,
    isHistorical,
    presentation,
    onSendMessage,
  }) => (
    <CommunicationsPreferencesConfirmationPrompt
      pendingActionId={pendingActionId}
      selectedAction={selectedAction}
      isHistorical={isHistorical}
      previewRows={
        isCommunicationsPreferencesPresentation(presentation)
          ? presentation.props.previewRows
          : []
      }
      onSendMessage={onSendMessage}
    />
  ),
  generic: ({
    pendingActionId,
    selectedAction,
    isHistorical,
    fallback,
    onSendMessage,
  }) => (
    <StagedActionConfirmationPrompt
      pendingActionId={pendingActionId}
      selectedAction={selectedAction}
      isHistorical={isHistorical}
      title={fallback.title}
      body={fallback.body}
      confirmButtonLabel={fallback.confirmButtonLabel}
      cancelButtonLabel={fallback.cancelButtonLabel}
      onSendMessage={onSendMessage}
    />
  ),
} satisfies Record<KnownConfirmationPromptRenderer, PromptRenderer>

const confirmationPromptRenderers: Partial<Record<string, PromptRenderer>> =
  knownConfirmationPromptRenderers

function ConfirmationPromptRenderer(props: ConfirmationPromptRendererProps) {
  const fallback = props.presentation.fallback
  const requestedRenderer = confirmationPromptRenderers[props.presentation.component]
  const renderPrompt = requestedRenderer ?? knownConfirmationPromptRenderers.generic

  return renderPrompt({
    ...props,
    fallback,
  })
}

export default ConfirmationPromptRenderer
